import { Button, Group, Modal, Pagination, Radio, Space, Table, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import TemplateDetailModal from '../../../Template/List/ui/templateDetailModal';
import { useDisclosure } from '@mantine/hooks';
import { queries } from '../../../../../../../shared/queries';

interface TemplateSelectModalProps {
  opened: boolean;
  close: () => void;
  companyId: number;
  selectedTemplateId: number | null;
  onSelect: (templateId: number, templateName: string) => void;
}

export default function TemplateSelectModal(props: TemplateSelectModalProps) {
  const { opened, close, companyId, selectedTemplateId, onSelect } = props;
  const [templateDetailOpened, { open: openTemplateDetail, close: closeTemplateDetail }] = useDisclosure(false);
  const [detailTemplateId, setDetailTemplateId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data: templateListData } = useQuery({
    ...queries.aiAgent.asCsTemplateList({
      companyId: companyId,
      pg: page,
      pgSize: pageSize,
      name: searchTerm || undefined,
    }),
  });
  const templates = templateListData?.result || [];
  const totalCount = templateListData?.count || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSelect = (templateId: number, templateName: string) => {
    onSelect(templateId, templateName);
  };

  const handleRowClick = (templateId: number, templateName: string) => {
    handleSelect(templateId, templateName);
  };

  const handleTemplateNameClick = (e: React.MouseEvent, templateId: number) => {
    e.stopPropagation();
    setDetailTemplateId(templateId);
    openTemplateDetail();
  };

  return (
    <Modal opened={opened} onClose={close} title="템플릿 선택" size="lg" centered>
      <TextInput
        placeholder="템플릿명을 검색해주세요"
        leftSection={<IconSearch size={16} />}
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.currentTarget.value);
          setPage(1); // 검색 시 첫 페이지로 리셋
        }}
        mb="md"
      />
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: '60px' }}>선택</Table.Th>
            <Table.Th style={{ width: '80px' }}>번호</Table.Th>
            <Table.Th>템플릿명</Table.Th>
            <Table.Th>상세설명</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {templates.length > 0 ? (
            templates.map(
              (
                template: {
                  id: number;
                  name: string;
                  description?: string;
                },
                index: number,
              ) => (
                <Table.Tr
                  key={template.id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: selectedTemplateId === template.id ? 'var(--mantine-color-blue-0)' : 'transparent',
                  }}
                  onClick={() => handleRowClick(template.id, template.name)}
                >
                  <Table.Td>
                    <Radio
                      checked={selectedTemplateId === template.id}
                      onChange={() => handleRowClick(template.id, template.name)}
                      onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
                    />
                  </Table.Td>
                  <Table.Td>{(page - 1) * pageSize + index + 1}</Table.Td>
                  <Table.Td>
                    <Text
                      td="underline"
                      size="sm"
                      style={{ cursor: 'pointer' }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleTemplateNameClick(e, template.id);
                      }}
                    >
                      {template.name}
                    </Text>
                  </Table.Td>
                  <Table.Td>{template.description || '-'}</Table.Td>
                </Table.Tr>
              ),
            )
          ) : (
            <Table.Tr>
              <Table.Td colSpan={4}>
                <Text c="dimmed" ta="center" py="xl">
                  {searchTerm ? '검색 결과가 없습니다.' : '템플릿이 없습니다.'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      <Space h="md" />
      <Group justify="center">
        <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
      </Group>
      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={close}>
          취소
        </Button>
        <Button
          onClick={() => {
            if (selectedTemplateId) {
              const template = templates.find((t: { id: number; name: string }) => t.id === selectedTemplateId);
              if (template) {
                handleSelect(template.id, template.name);
              }
            }
          }}
          disabled={!selectedTemplateId}
        >
          선택
        </Button>
      </Group>
      {templateDetailOpened && detailTemplateId && (
        <TemplateDetailModal
          opened={templateDetailOpened}
          onClose={closeTemplateDetail}
          templateId={detailTemplateId}
          headerIndex={templateListData?.result.find((t) => t.id === detailTemplateId)?.headerIndex || 0}
        />
      )}
    </Modal>
  );
}
